import strings from 'common/Translation/Translate';
import type { KeyboardEvent } from 'react';
import { Validators } from '../../utils/validators';

export type PaymentScheduleValues = {
  minConfirmPaymentPercentage?: number | string | null;
  paymentSlotOnePercentage?: number | string | null;
  paymentSlotTwoPercentage?: number | string | null;
  paymentSlotThreePercentage?: number | string | null;
  paymentSlotOneBeforeDays?: number | string | null;
  paymentSlotTwoBeforeDays?: number | string | null;
  fullPaymentBeforeDays?: number | string | null;
};

export type PaymentScheduleRow =
  | {
      id: string;
      label: string;
      percentField: keyof PaymentScheduleValues;
      dueType: 'on_confirmation';
    }
  | {
      id: string;
      label: string;
      percentField: keyof PaymentScheduleValues;
      daysField: keyof PaymentScheduleValues;
      dueType: 'days_before';
    };

export const getPaymentScheduleRows = (): PaymentScheduleRow[] => [
  {
    id: 'confirmation',
    label: strings.payment_schedule_confirmation,
    percentField: 'minConfirmPaymentPercentage',
    dueType: 'on_confirmation',
  },
  {
    id: 'slot1',
    label: strings.payment_schedule_first_instalment,
    percentField: 'paymentSlotOnePercentage',
    daysField: 'paymentSlotOneBeforeDays',
    dueType: 'days_before',
  },
  {
    id: 'slot2',
    label: strings.payment_schedule_second_instalment,
    percentField: 'paymentSlotTwoPercentage',
    daysField: 'paymentSlotTwoBeforeDays',
    dueType: 'days_before',
  },
  {
    id: 'slot3',
    label: strings.payment_schedule_final_payment,
    percentField: 'paymentSlotThreePercentage',
    daysField: 'fullPaymentBeforeDays',
    dueType: 'days_before',
  },
];

export const PAYMENT_SCHEDULE_FIELD_KEYS: (keyof PaymentScheduleValues)[] = [
  'minConfirmPaymentPercentage',
  'paymentSlotOnePercentage',
  'paymentSlotTwoPercentage',
  'paymentSlotThreePercentage',
  'paymentSlotOneBeforeDays',
  'paymentSlotTwoBeforeDays',
  'fullPaymentBeforeDays',
];

export const pickPaymentScheduleValues = (source: Record<string, unknown>): PaymentScheduleValues =>
  PAYMENT_SCHEDULE_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = source[key as string] as PaymentScheduleValues[keyof PaymentScheduleValues];
    return acc;
  }, {} as PaymentScheduleValues);

const validatorMessage = (message: string | (string | number)[]): string =>
  Array.isArray(message) ? message.map(String).join('') : message;

/** Strip invalid characters and clamp to a non-negative integer (empty allowed while typing). */
export const sanitizeNonNegativeIntInput = (raw: string, max: number): string => {
  if (raw === '') return '';
  const digitsOnly = raw.replace(/\D/g, '');
  if (digitsOnly === '') return '';
  return String(Math.min(parseInt(digitsOnly, 10), max));
};

export const formatPaymentDueLabel = (days: unknown): string => {
  if (days === '' || days == null) return strings.payment_schedule_days_before;
  const parsed = parseInt(String(days), 10);
  if (!Number.isFinite(parsed)) return strings.payment_schedule_days_before;
  if (parsed === 0) return strings.payment_schedule_on_event_day;
  return strings.payment_schedule_days_before;
};

export const blockNegativeNumberKeys = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
    event.preventDefault();
  }
};

const validateOptionalPercent = (value: unknown, field: string, errors: Record<string, string>) => {
  if (value === '' || value == null) return;
  const result = Validators.validateInt(String(value), '', 0, 100);
  if (result && result.error) {
    errors[field] = validatorMessage(result.message);
  }
};

const validateRequiredDays = (value: unknown, field: string, errors: Record<string, string>) => {
  const mandatory = Validators.validateMandatoryField(String(value ?? ''), '');
  if (mandatory && mandatory.error) {
    errors[field] = validatorMessage(mandatory.message);
    return;
  }
  const result = Validators.validateInt(String(value), '', 0, 999);
  if (result && result.error) {
    errors[field] = validatorMessage(result.message);
  }
};

export const validatePaymentSchedule = (
  values: PaymentScheduleValues,
): { valid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  validateOptionalPercent(values.minConfirmPaymentPercentage, 'minConfirmPaymentPercentage', errors);
  validateOptionalPercent(values.paymentSlotOnePercentage, 'paymentSlotOnePercentage', errors);
  validateOptionalPercent(values.paymentSlotTwoPercentage, 'paymentSlotTwoPercentage', errors);
  validateOptionalPercent(values.paymentSlotThreePercentage, 'paymentSlotThreePercentage', errors);

  validateRequiredDays(values.paymentSlotOneBeforeDays, 'paymentSlotOneBeforeDays', errors);
  validateRequiredDays(values.paymentSlotTwoBeforeDays, 'paymentSlotTwoBeforeDays', errors);
  validateRequiredDays(values.fullPaymentBeforeDays, 'fullPaymentBeforeDays', errors);

  return { valid: Object.keys(errors).length === 0, errors };
};
